import {
  CommonModule,
  DATE_PIPE_DEFAULT_OPTIONS,
  DatePipe,
} from '@angular/common';
import {
  AfterViewInit,
  Component,
  computed,
  inject,
  ViewChild,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatChipsModule } from '@angular/material/chips';
import { provideNativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import {
  MatPaginatorModule,
  type PageEvent,
} from '@angular/material/paginator';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatSortModule, type Sort } from '@angular/material/sort';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Router } from '@angular/router';
import debounce from 'lodash/debounce';
import {
  RequestsTableComponent,
  RequestTableAction,
  RequestTableConfig,
  RequestTableItem,
} from '../../shared/components/requests-table/requests-table.component';
import { EnhancedLoadingService } from '../../shared/services/enhanced-loading.service';
import { SmartOrpcClientService } from '../../shared/services/smart-orpc-client.service';
import { UserService } from '../../shared/services/user.service';

export interface SearchParams {
  onlyMine?: boolean;
  searchText?: string;
  range?: {
    start?: Date;
    end?: Date;
  };
}

import type { BatchHistoriesSafrasRequest } from '#backend/entity/loaded/harvest-history/BatchHistoriesSafrasRequest';
import { DatasafraSearchComponent } from '../../shared/components/datasafra-search/datasafra-search.component';
import { UserRole } from '#backend/common/user-types';
import { createNestedOrder } from '../../shared/utils/create-nested-order';

type StatusCode = BatchHistoriesSafrasRequest['status']['code'];

export interface HistoriesSafrasRequest extends RequestTableItem {
  organizationName?: string | null;
  userName?: string | null;
  numRelatoriosSolicitacao?: number;
  numRelatoriosExecutados?: number;
  dataEvento?: Date;
  motivoCancelamento?: string;
}

@Component({
  selector: 'app-histories-safras',
  standalone: true,
  imports: [
    CommonModule,
    DatasafraSearchComponent,
    DatePipe,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatChipsModule,
    MatDatepickerModule,
    MatDividerModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatMenuModule,
    MatPaginatorModule,
    MatProgressBarModule,
    MatSortModule,
    MatTableModule,
    MatTooltipModule,
    ReactiveFormsModule,
    RequestsTableComponent,
  ],
  templateUrl: './histories-safras.component.html',
  styleUrl: './histories-safras.component.scss',
  providers: [
    provideNativeDateAdapter(),
    {
      provide: DATE_PIPE_DEFAULT_OPTIONS,
      useValue: { dateFormat: 'short', timezone: '-03:00' },
    },
  ],
})
export class HistoriesSafrasComponent implements AfterViewInit {
  userService = inject(UserService);
  orpc = inject(SmartOrpcClientService);
  loadingService = inject(EnhancedLoadingService);

  dataSource = new MatTableDataSource<HistoriesSafrasRequest>([]);

  // Observables para diferentes tipos de loading
  globalLoading$ = this.loadingService.global$;
  gridLoading$ = this.loadingService.grid$;
  inputLoading$ = this.loadingService.input$;
  buttonLoading$ = this.loadingService.button$;

  get displayedColumns(): string[] {
    const columns = [
      'id',
      ...(this.userService.isGeoambiente ? ['cliente'] : []),
      'createdBy',
      'createdDate',
      'status',
    ];

    if (this.userService.isGeoambiente) {
      columns.push('consistency');
    }

    return columns;
  }
  // Configuração da tabela
  tableConfig: RequestTableConfig = {
    showExpandColumn: true,
    showActionsColumn: true,
    showViewAdminColumn: this.userService.isGeoambiente,
    columns: [...this.displayedColumns],
    statusClassFunction: (statusCode: StatusCode) =>
      this.getStatusClass(statusCode),
    initialSort: { active: 'createdDate', direction: 'desc' },
  };

  // Ações da tabela
  tableActions: RequestTableAction[] = [
    {
      label: 'Acessar resultados',
      icon: 'description',
      action: (item: HistoriesSafrasRequest) =>
        this.goToHistoriesReportsPage(item),
      condition: (item: HistoriesSafrasRequest) =>
        item.status.code === 'COMPLETED',
    },
  ];

  @ViewChild(DatasafraSearchComponent)
  declare search: DatasafraSearchComponent;
  @ViewChild(RequestsTableComponent)
  declare requestTable: RequestsTableComponent;

  abortLoad: AbortController | undefined;
  length = 0;

  // Armazena os últimos parâmetros de busca para manter estado na paginação
  private lastSearchParams: SearchParams = {};

  debouncedLoad = debounce((v: SearchParams) => this.load(v), 300, {
    leading: true,
  });

  constructor(private router: Router) {}

  asTypedItem = (x: any) => x as RequestTableItem;

  navigateToCreatePage(): void {
    this.router.navigate(['/histories-safras/new']);
  }

  ngAfterViewInit() {
    this.load({});
  }

  handleSearch(params: SearchParams) {
    this.lastSearchParams = params; // Armazena os parâmetros
    this.requestTable.paginator.firstPage();
    this.load(params);
  }

  onSortChange(event: Sort) {
    this.debouncedLoad(this.lastSearchParams); // Usa os parâmetros armazenados
  }

  onPageChange(event: PageEvent) {
    this.debouncedLoad(this.lastSearchParams); // Usa os parâmetros armazenados
  }

  onRefresh() {
    this.debouncedLoad(this.lastSearchParams); // Usa os parâmetros armazenados
  }

  private async load(params: SearchParams) {
    this.abortLoad?.abort();
    this.abortLoad = new AbortController();

    const [result, count] = await this.orpc.client.historiesSafras.list(
      {
        page: {
          cursor:
            (this.requestTable.paginator?.pageIndex ?? 0) *
            (this.requestTable.paginator?.pageSize ?? 0),
          limit: this.requestTable.paginator?.pageSize ?? 0,
        },
        where: {
          textSearch: params.searchText ?? undefined,
          dateRange: {
            from: params.range?.start ?? undefined,
            to: params.range?.end ?? undefined,
          },
          onlyMine: params.onlyMine ?? true,
        },
        orderBy: this.dataSource.sort?.active
          ? createNestedOrder(this.dataSource.sort)
          : undefined,
      },
      { signal: this.abortLoad.signal },
    );
    this.dataSource.data = result;
    this.length = count;
  }

  /**
   * Retorna a classe CSS baseada no status do histórico de safra
   */
  getStatusClass(status: StatusCode): string {
    switch (status) {
      case 'VALIDATING_INPUT':
      case 'ANALYZING':
        return 'chip-validando';
      case 'COMPLETED':
        return 'chip-concluida';
      case 'FAILED':
        return 'chip-cancelada';
      default:
        return '';
    }
  }

  relatorioHistory(element: HistoriesSafrasRequest): void {
    // TODO: Implementar navegação para relatório
  }

  goToHistoriesReportsPage(element: HistoriesSafrasRequest) {
    this.router.navigate(['histories-safras/reports'], {
      queryParams: { batchRequestId: element.id },
    });
  }

  onRowToggle(element: HistoriesSafrasRequest): void {
    // Pode ser usado para lógica adicional quando uma linha é expandida
  }

  handleViewAdmin(item: HistoriesSafrasRequest): void {
    // Navega para a página de detalhes admin do histories-safras
    this.router.navigate(['/histories-safras/adm/detail', item.id]);
  }

  canCreateRequest = computed(() => {
    const user = this.userService.user();
    if (!user) return false;
    return [
      UserRole.ADMIN_CLIENTE,
      UserRole.OPERADOR_GEOAMBIENTE,
      UserRole.ADMIN_GEOAMBIENTE,
    ].includes(user.role);
  });
}
